import { Alignment, Color, DOMUtil, ValueDisplayValue } from "@mcleod/core";
import { ComponentConstructor, Event } from "../..";
import { ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { RadioBoxOption } from "./RadioBoxOption";
import { RadioBoxOptionSelectionEvent } from "../../events/RadioBoxOptionSelectionEvent";
import { RadioBoxPropDefinitions, RadioBoxProps } from "./RadioBoxProps";
import { RadioGroup } from "./RadioGroup";
import { RadioOption } from "./RadioOption";
import { RadioOptionResolver } from "./RadioOptionResolver";
import { RadioOptionSelectionEvent } from "../../events/RadioOptionSelectionEvent";

export class RadioBox extends RadioGroup implements RadioBoxProps {
    private _captionAlignment: Alignment.TOP | Alignment.LEFT;
    private _selectedBackgroundColor: Color;
    private _selectedTextColor: Color;
    private _unselectedBackgroundColor: Color;
    private _unselectedTextColor: Color;
    
    constructor(props?: Partial<RadioBoxProps>) {
        super(props);
    }

    public get captionAlignment(): Alignment.TOP | Alignment.LEFT {
        return this._captionAlignment ?? this.getPropertyDefinitions().captionAlignment.defaultValue;
    }

    public set captionAlignment(value: Alignment.TOP | Alignment.LEFT) {
        this._captionAlignment = value;
        if (value === Alignment.LEFT) {
            this._element.style.display = "flex";
            this._element.style.flexDirection = "row";
            this._element.style.alignItems = "center";
            this.captionLabel.marginRight = 4;
        }
        else {
            this._element.style.display = "unset";
            this._element.style.alignItems = "";
            this.captionLabel.marginRight = 0;
        }
    }

    override get options(): RadioBoxOption[] {
        return super.options as RadioBoxOption[];
    }

    get selectedOption(): RadioBoxOption {
        return super.selectedOption as RadioBoxOption;
    }

    set selectedOption(value: RadioBoxOption) {
        super.selectedOption = value;
    }

    public override addOptions(...value: (ValueDisplayValue|RadioOption)[]) {
        super.addOptions(...value);
        this.syncHeights();
        this.syncBorders();
    }

    private syncHeights() {
        let maxOptionHeight = 0;
        let minDisplayValueLabelHeight = 9999;
        this.options?.forEach(option => {
            option.doWhileDimensionsComputed((option: RadioBoxOption) => {
                maxOptionHeight = Math.max(maxOptionHeight, DOMUtil.getElementHeight(option._element));
                minDisplayValueLabelHeight = Math.min(minDisplayValueLabelHeight,
                    DOMUtil.getElementHeight(option.labelDisplayValue._element));
            });
        });
        this.options?.forEach(option => {
            option.height = maxOptionHeight;
            option.labelDisplayValue.imageHeight = minDisplayValueLabelHeight - 8;
            option.labelDisplayValue.imageWidth = minDisplayValueLabelHeight - 8;
        });
    }

    private syncBorders() {
        this.options?.forEach((option, index, array) => option.syncBorders(index === 0, index === (array.length-1)));
    }

    override get optionClass(): ComponentConstructor {
        return RadioBoxOption;
    }

    override resolveOption(value: ValueDisplayValue | RadioOption): RadioOption {
        return RadioOptionResolver.resolveOption(this, value);
    }

    override resolveOptions(value: ValueDisplayValue[] | (() => ValueDisplayValue[]) | RadioOption[] |
        (() => RadioOption[])): RadioOption[]{
        return RadioOptionResolver.resolveOptions(this, value);
    }

    override createSelectionEvent(oldSelection: RadioOption, newSelection: RadioOption, originatingEvent?: Event):
        RadioOptionSelectionEvent {
        return new RadioBoxOptionSelectionEvent(this, oldSelection, newSelection, originatingEvent)
    }

    get selectedBackgroundColor(): Color {
        return this._selectedBackgroundColor ?? this.getPropertyDefinitions().selectedBackgroundColor.defaultValue;
    }

    set selectedBackgroundColor(value: Color) {
        this._selectedBackgroundColor = value;
        if (this.selectedOption != null)
            this.selectedOption.backgroundColor = this.selectedBackgroundColor;
    }

    get selectedTextColor(): Color {
        return this._selectedTextColor ?? this.getPropertyDefinitions().selectedTextColor.defaultValue;
    }

    set selectedTextColor(value: Color) {
        this._selectedTextColor = value;
        if (this.selectedOption != null)
            this.selectedOption.color = this.selectedTextColor;
    }

    get unselectedBackgroundColor(): Color {
        return this._unselectedBackgroundColor ?? this.getPropertyDefinitions().unselectedBackgroundColor.defaultValue;
    }

    set unselectedBackgroundColor(value: Color) {
        this._unselectedBackgroundColor = value;
        this.options?.forEach(option => {
            if (option.selected !== true)
                option.backgroundColor = this.unselectedBackgroundColor;
        });
    }

    get unselectedTextColor(): Color {
        return this._unselectedTextColor ?? this.getPropertyDefinitions().unselectedTextColor.defaultValue;
    }

    set unselectedTextColor(value: Color) {
        this._unselectedTextColor = value;
        this.options?.forEach(option => {
            if (option.selected !== true)
                option.color = this.unselectedTextColor;
        });
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return RadioBoxPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "radiobox";
    }

    override get properName(): string {
        return "Radio Box";
    }
}
ComponentTypes.registerComponentType("radiobox", { constructor: RadioBox.prototype.constructor });
